<template>
    <div class="w-100 h-100 position-relative">
        <div v-if="loadingLocations && !webglErrorMessage" class="map-loading-indicator shadow-sm">
            <b-spinner small class="mr-2"></b-spinner>
            {{ $t('vendorLocationMap.loadingLocations') }}
        </div>
        <div v-if="webglErrorMessage" class="map-webgl-error d-flex flex-column align-items-center justify-content-center h-100 text-center">
            <b-icon icon="map" font-scale="2" class="text-muted mb-3"></b-icon>
            <h5 class="text-muted">{{ $t('vendorLocationMap.mapUnavailable') }}</h5>
            <p class="text-muted mb-0">
                {{ webglErrorMessage }}
            </p>
            <p class="text-muted mt-2">
                {{ $t('vendorLocationMap.tryEnablingAcceleration') }}
            </p>
        </div>
        <div v-else ref="mapContainer" class="map-container"></div>
    </div>
</template>


<script>
import Vue from 'vue';
import VendorLocationMapPopup from '@/components/vendors/components/VendorLocationMapPopup.vue';
import { suppliosApi } from '@/js/api-service';
import { errorHandling, splUtilsMixin, tenantInfoMixin } from '@/js/custom-vue-mixins';

class CompanyLocation {
    /// intended to match the backend provided data from list_for_map -- CompanyLocation or CompanySite (use_vendor_sites tenants), same shape
    constructor(data) {
        this.id = data.id;
        this.name = data.name;
        this.lat = data.lat;
        this.lng = data.lng;
        this.company = {
            id: data.company.id,
            name: data.company.name,
            url: data.company.url,
        };
        this.is_primary = data.is_primary || false;
        this.site_type_ids = data.site_type_ids || [];  // CompanySite only
    }

    get lngLat() {
        return [parseFloat(this.lng), parseFloat(this.lat)];
    }
    get label() {
        return [this.company.name, this.name].filter(Boolean).join(' - ');
    }
    get isValid() {
        const lat = parseFloat(this.lat);
        const lng = parseFloat(this.lng);
        return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    }
}

const SOURCE_ID = 'company-locations';
const LAYER_CLUSTERS = 'clusters';
const LAYER_CLUSTER_COUNT = 'cluster-count';
const LAYER_POINTS = 'unclustered-point';
const CLUSTER_MAX_ZOOM = 14;

// the app's accent (cyan) scale -- --accent-* CSS vars -- maplibre paint props need literal colors, not CSS vars
const ACCENT_300 = '#53c0e1';
const ACCENT_400 = '#2ab2da';
const ACCENT_500 = '#02a3d3';
const ACCENT_700 = '#016f8f';
const ACCENT_900 = '#013b4c';
const SAME_SPOT_DEGREES = 0.0001;  // ~11m -- clustered points closer than this are listed in a popup, since zooming in will never separate them

const CPH_LAT_LNG = [12.5683, 55.6761];  // Copenhagen - 55.6761° N, 12.5683° E
const DETROIT_LAT_LNG = [-83.0425, 42.3297];  // Detroit - 42.3297° N, 83.0425° W

export default {
    name: 'VendorLocationMap',
    components: {},
    mixins: [errorHandling, splUtilsMixin, tenantInfoMixin],
    i18n: {
        messages: {
            en: {
                vendorLocationMap: {
                    mapUnavailable: 'Map Unavailable',
                    tryEnablingAcceleration: 'Try enabling graphics acceleration in your browser settings, or use a different browser.',
                    webglNotSupported: 'WebGL is not supported by your browser.',
                    webglAccelerationOff: 'WebGL is supported by your browser, but you likely have graphics acceleration turned off or your GPU is not supported.',
                    webglTurnedOff: 'WebGL is turned off in your browser so we cannot show the map.',
                    errorLoadingLocations: 'Error loading locations',
                    loadingLocations: 'Loading supplier locations...',
                },
            },
            es: {
                vendorLocationMap: {
                    mapUnavailable: 'Mapa no disponible',
                    tryEnablingAcceleration: 'Intente habilitar la aceleración de gráficos en la configuración de su navegador o utilice otro navegador.',
                    webglNotSupported: 'Su navegador no es compatible con WebGL.',
                    webglAccelerationOff: 'Su navegador es compatible con WebGL, pero es probable que la aceleración de gráficos esté desactivada o que su GPU no sea compatible.',
                    webglTurnedOff: 'WebGL está desactivado en su navegador, por lo que no podemos mostrar el mapa.',
                    errorLoadingLocations: 'Error al cargar las ubicaciones',
                    loadingLocations: 'Cargando ubicaciones de proveedores...',
                },
            },
            pt: {
                vendorLocationMap: {
                    mapUnavailable: 'Mapa indisponível',
                    tryEnablingAcceleration: 'Tente ativar a aceleração gráfica nas configurações do seu navegador ou use outro navegador.',
                    webglNotSupported: 'Seu navegador não é compatível com WebGL.',
                    webglAccelerationOff: 'Seu navegador é compatível com WebGL, mas a aceleração gráfica provavelmente está desativada ou sua GPU não é compatível.',
                    webglTurnedOff: 'O WebGL está desativado no seu navegador, por isso não podemos exibir o mapa.',
                    errorLoadingLocations: 'Erro ao carregar as localizações',
                    loadingLocations: 'Carregando localizações de fornecedores...',
                },
            },
            'zh-Hans': {
                vendorLocationMap: {
                    mapUnavailable: '地图不可用',
                    tryEnablingAcceleration: '请尝试在浏览器设置中启用图形加速，或使用其他浏览器。',
                    webglNotSupported: '您的浏览器不支持 WebGL。',
                    webglAccelerationOff: '您的浏览器支持 WebGL，但您可能已关闭图形加速，或您的 GPU 不受支持。',
                    webglTurnedOff: '您的浏览器已关闭 WebGL，因此无法显示地图。',
                    errorLoadingLocations: '加载地点时出错',
                    loadingLocations: '正在加载供应商地点...',
                },
            },
            'zh-Hant': {
                vendorLocationMap: {
                    mapUnavailable: '地圖無法使用',
                    tryEnablingAcceleration: '請嘗試在瀏覽器設定中啟用圖形加速，或使用其他瀏覽器。',
                    webglNotSupported: '您的瀏覽器不支援 WebGL。',
                    webglAccelerationOff: '您的瀏覽器支援 WebGL，但您可能已關閉圖形加速，或您的 GPU 不受支援。',
                    webglTurnedOff: '您的瀏覽器已關閉 WebGL，因此無法顯示地圖。',
                    errorLoadingLocations: '載入地點時發生錯誤',
                    loadingLocations: '正在載入供應商地點...',
                },
            },
        },
    },
    props: {},
    computed: {
        usesVendorSites() {
            return this.$getTenantFlag('use_vendor_sites', false);
        },
    },
    data() {
        return {
            map: null,
            resizeObserver: null,
            resizeTimer: null,
            loadingLocations: false,
            locationsRequest: null,  // promise from getLocations() -- started on mount, so it runs while the map style/tiles load
            locations: [],
            siteTypesById: {},  // { id: serialized CompanySiteType } -- use_vendor_sites tenants only, for the badges in popups
            webglErrorMessage: '',  // populate if we want to show more specific error messages about WebGL issues in the future
        };
    },
    created() {
        // NOT reactive (not in data) -- no need for Vue to observe these
        this.locationsById = new Map();  // for popups -- feature properties only carry the locationId
        this.mapPopup = null;  // the open maplibre Popup, if any
    },
    mounted() {
        this.$nextTick(() => {
            this.checkWebglSupported();  // that will set this.webglErrorMessage if there is a problem
            if (this.webglErrorMessage) {
                return;
            }
            this.locationsRequest = this.getLocations();  // don't wait for the map 'load' event to start fetching
            this.setMapHeight();
            this.initMap();
            this.initResizeObserver();
        });
    },
    beforeDestroy() {
        clearTimeout(this.resizeTimer);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.closePopup();  // destroys the popup's Vue instance too
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },
    methods: {
        checkWebglSupported() {
            // if there is a problem, set this.webglErrorMessage to a non-empty string and return
            // check to see if webgl is turned on in this browser, from docs:  https://maplibre.org/maplibre-gl-js/docs/examples/check-if-webgl-is-supported/
            if (!window.WebGLRenderingContext) {
                // if we don't have this, then no chance
                const noWebGLRenderingContextMsg = this.$t('vendorLocationMap.webglNotSupported');
                console.warn(noWebGLRenderingContextMsg);
                this.webglErrorMessage = noWebGLRenderingContextMsg;
                return;
            }
            const canvas = document.createElement('canvas');
            try {
                // Note that { failIfMajorPerformanceCaveat: true } can be passed as a second argument
                // to canvas.getContext(), causing the check to fail if hardware rendering is not available. See
                // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/getContext
                // for more details.
                const getContextOpts = { failIfMajorPerformanceCaveat: true };
                const context = canvas.getContext('webgl2', getContextOpts) || canvas.getContext('webgl', getContextOpts);
                if (context && typeof context.getParameter === 'function') {
                    console.log('WebGL is supported and seems to be working fine.');
                    this.webglErrorMessage = '';  // clear any error message just in case
                    return;
                } else {
                    const hardwareAccTurnedOffMsg = this.$t('vendorLocationMap.webglAccelerationOff');
                    console.warn(hardwareAccTurnedOffMsg);
                    this.webglErrorMessage = hardwareAccTurnedOffMsg;
                    return;
                }
            } catch (e) {
                // WebGL supported but disabled
                const webglDisabledMsg = this.$t('vendorLocationMap.webglTurnedOff');
                console.warn(webglDisabledMsg);
                this.webglErrorMessage = webglDisabledMsg;
                return;
            }
        },
        initMap() {
            const defaultCenterBySuppliosRegion = {
                'eu-central-1': CPH_LAT_LNG,
                'us-east-1': DETROIT_LAT_LNG,
                'local-dev': DETROIT_LAT_LNG,
            };
            const currentRegion = this.$splData.SUPPLIOS_REGION || 'local-dev';
            const initialCenter = defaultCenterBySuppliosRegion[currentRegion] || CPH_LAT_LNG;
            this.map = new window.maplibregl.Map({
                container: this.$refs.mapContainer,
                style: 'https://tiles.stadiamaps.com/styles/alidade_smooth_dark.json',
                // center: [12, 53],   // Europe - 42.3297° N, 83.0425° W is Detroit
                center: initialCenter,
                zoom: 4,
                maxZoom: 18,
                minZoom: 2,
                attributionControl: true,
            });
            this.map.addControl(new window.maplibregl.NavigationControl(), 'top-right');

            this.map.once('load', () => {
                this.initSourceAndLayers();
                this.setHandlers();
                this.loadAndSetLocations();
                this.centerOnUserLocation();
            });
        },
        centerOnUserLocation() {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log('User location:', position.coords);
                    if (!this.map) {
                        return;  // in case the map was unmounted while waiting for geolocation (user switches tabs/views)
                    }
                    this.map.easeTo({
                        center: [position.coords.longitude, position.coords.latitude],
                        zoom: 6,
                    });
                },
                (err) => {
                    console.warn('Geolocation unavailable:', err.message);
                },
                { timeout: 5000 },
            );
        },
        initSourceAndLayers() {
            if (!this.map) return;  // in case this is called after the map was unmounted for some reason
            this.map.addSource(SOURCE_ID, {
                type: 'geojson',
                data: this.buildGeoJson([]),
                cluster: true,
                clusterMaxZoom: CLUSTER_MAX_ZOOM,
                clusterRadius: 50,
            });

            // Cluster circles
            this.map.addLayer({
                id: LAYER_CLUSTERS,
                type: 'circle',
                source: SOURCE_ID,
                filter: ['has', 'point_count'],
                paint: {
                    // one hue, lighter = more locations (dark basemap, so the biggest clusters stand out) -- same steps as the radius
                    'circle-color': [
                        'step', ['get', 'point_count'],
                        ACCENT_700, 10,
                        ACCENT_500, 50,
                        ACCENT_300,
                    ],
                    'circle-radius': [
                        'step', ['get', 'point_count'],
                        20, 10,
                        30, 50,
                        40,
                    ],
                    'circle-opacity': 0.85,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': 'rgba(255, 255, 255, 0.5)',
                },
            });

            // Cluster count labels
            this.map.addLayer({
                id: LAYER_CLUSTER_COUNT,
                type: 'symbol',
                source: SOURCE_ID,
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-size': 12,
                },
                paint: {
                    // dark text on the lightest step -- white on ACCENT_300 is too low contrast
                    'text-color': [
                        'step', ['get', 'point_count'],
                        '#ffffff', 50,
                        ACCENT_900,
                    ],
                },
            });

            // Individual points
            this.map.addLayer({
                id: LAYER_POINTS,
                type: 'circle',
                source: SOURCE_ID,
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-color': ACCENT_400,  // a bit brighter than the smallest clusters, so single sites still stand out
                    'circle-radius': 6,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#fff',
                },
            });
        },
        setHandlers() {
            // Click cluster → zoom to expand -- unless zooming in will never split it (locations on the same spot), then list them
            this.map.on('click', LAYER_CLUSTERS, async (e) => {
                const feature = e.features[0];
                const clusterId = feature.properties.cluster_id;
                const source = this.map.getSource(SOURCE_ID);
                const zoom = await source.getClusterExpansionZoom(clusterId);
                if (!this.map) return;  // in case the map was unmounted while waiting
                if (zoom > CLUSTER_MAX_ZOOM) {
                    // the cluster only splits past CLUSTER_MAX_ZOOM -- if all its points are on the same spot, they would still overlap there
                    const leaves = await source.getClusterLeaves(clusterId, feature.properties.point_count, 0);
                    if (!this.map) return;
                    if (this.allOnSameSpot(leaves)) {
                        this.showLocationsPopup([...leaves[0].geometry.coordinates], leaves);
                        return;
                    }
                }
                this.map.easeTo({ center: feature.geometry.coordinates, zoom });
            });

            // Click individual point → show popup for ALL points under the click (overlapping dots), not just the top one
            this.map.on('click', LAYER_POINTS, (e) => {
                if (!this.map) return;  // in case the map was unmounted while waiting for click event
                const coordinates = [...e.features[0].geometry.coordinates];
                this.showLocationsPopup(coordinates, e.features);
            });

            // Pointer cursor on interactive layers
            this.map.on('mouseenter', LAYER_CLUSTERS, () => { this.map.getCanvas().style.cursor = 'pointer'; });
            this.map.on('mouseleave', LAYER_CLUSTERS, () => { this.map.getCanvas().style.cursor = ''; });
            this.map.on('mouseenter', LAYER_POINTS, () => { this.map.getCanvas().style.cursor = 'pointer'; });
            this.map.on('mouseleave', LAYER_POINTS, () => { this.map.getCanvas().style.cursor = ''; });
        },
        allOnSameSpot(features) {
            const [firstLng, firstLat] = features[0].geometry.coordinates;
            return features.every(f => {
                const [lng, lat] = f.geometry.coordinates;
                return Math.abs(lng - firstLng) < SAME_SPOT_DEGREES && Math.abs(lat - firstLat) < SAME_SPOT_DEGREES;
            });
        },
        showLocationsPopup(coordinates, features) {
            // the popup content is a real Vue component (VendorLocationMapPopup) mounted into the maplibre Popup with setDOMContent
            // parent: this -- so it gets the same root (i18n, etc.) -- and it is destroyed when the popup closes
            // de-dupe by locationId -- maplibre can return the same point twice where it crosses a tile boundary
            const locationIds = [...new Set(features.map(f => f.properties.locationId))];
            const locations = locationIds.map(id => this.locationsById.get(id)).filter(Boolean);
            if (!locations.length) return;
            this.closePopup();
            const PopupContent = Vue.extend(VendorLocationMapPopup);
            const popupVm = new PopupContent({
                parent: this,
                propsData: { locations, siteTypesById: this.siteTypesById },
            }).$mount();
            const popup = new window.maplibregl.Popup({ maxWidth: '320px' })
                .setLngLat(coordinates)
                .setDOMContent(popupVm.$el)
                .addTo(this.map);
            popup.on('close', () => {
                popupVm.$destroy();
                if (this.mapPopup === popup) {
                    this.mapPopup = null;
                }
            });
            this.mapPopup = popup;
        },
        closePopup() {
            if (this.mapPopup) {
                this.mapPopup.remove();  // fires 'close' -> destroys the Vue instance
            }
        },
        buildGeoJson(locations) {
            // properties only carry the locationId -- popups look up the full location in this.locationsById
            // (maplibre turns nested objects/arrays in properties into JSON strings, so don't put site_type_ids etc. here)
            return {
                type: 'FeatureCollection',
                features: locations.map(loc => ({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: loc.lngLat },
                    properties: { locationId: loc.id },
                })),
            };
        },
        setMapHeight() {
            const mapContainer = this.$refs.mapContainer;
            if (!mapContainer) return;
            const availableHeight = this.$el.clientHeight;
            const availableWidth = this.$el.clientWidth;
            console.log(`Setting map height: availableHeight=${availableHeight}, availableWidth=${availableWidth}`);
            mapContainer.style.height = availableHeight + 'px';
            mapContainer.style.width = availableWidth + 'px';
        },
        initResizeObserver() {
            this.resizeObserver = new ResizeObserver(() => {
                clearTimeout(this.resizeTimer);
                this.resizeTimer = setTimeout(() => this.handleResize(), 250);
            });
            this.resizeObserver.observe(document.body);
        },
        handleResize() {
            this.setMapHeight();
            if (this.map) {
                this.map.resize();
            }
        },
        async loadAndSetLocations() {
            try {
                await (this.locationsRequest || this.getLocations());
                this.updateMapLocations();
            } catch (error) {
                console.error('Error loading locations:', error);
            }
        },
        async getLocations() {
            this.loadingLocations = true;
            try {
                // use_vendor_sites tenants: CompanySite instead of CompanyLocation -- plus the site types, for the badges in popups
                const api = this.usesVendorSites ? suppliosApi.companySite : suppliosApi.companyLocation;
                const [resp, siteTypesResp] = await Promise.all([
                    api.list_for_map(),
                    this.usesVendorSites ? suppliosApi.companySiteType.list() : Promise.resolve(null),
                ]);
                this.locations = resp.data.map(loc => new CompanyLocation(loc));
                this.locationsById = new Map(this.locations.map(loc => [loc.id, loc]));
                this.siteTypesById = siteTypesResp ? Object.fromEntries(siteTypesResp.data.map(st => [st.id, st])) : {};
            } catch (error) {
                this.apiErrorHandler(error, this.$t('vendorLocationMap.errorLoadingLocations'));
            }
            this.loadingLocations = false;
        },
        updateMapLocations() {
            if (!this.map) return;
            const source = this.map.getSource(SOURCE_ID);
            if (!source) return;

            const validLocations = this.locations.filter(loc => {
                if (!loc.isValid) {
                    // this will happen often for non-geocoded stuff... no need to log.
                    // console.warn(`Invalid coordinates for location ${loc.label}`);
                    return false;
                }
                return true;
            });
            source.setData(this.buildGeoJson(validLocations));
        },
    },
};
</script>


<style lang="scss">
.map-container {
    width: 100%;
    margin: 0;
}

.map-loading-indicator {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;  // above the maplibre canvas
    display: flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    color: #333;
    font-size: 0.85rem;
    pointer-events: none;  // don't block map interaction while loading
}
</style>
